import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, } from 'react';
import { View, Keyboard, } from 'react-native';
import getSymbolFromCurrency from 'currency-symbol-map';
import Button from 'design-system/Button';
import { IconBoldCalendar, IconCoins } from 'design-system/icons';
import StatusBar from 'design-system/StatusBar';
import Text, { styles } from 'design-system/Text';
import ToolTip from 'design-system/ToolTip';
import { AppView } from 'design-system/AppView';
import { SettingsSection } from 'design-system/SettingsSection';
import { SettingsItemDescription, SettingsItemNavigate, SettingsItemSubDescription, SettingsItemTitle, } from 'design-system/SettingsItem';
import { HelpCallToAction } from 'design-system/HelpCallToAction';
import Spacer from 'design-system/Spacer';
import { rem } from 'design-system/values';
import { AnimatedKeyboardAwareScrollView } from 'design-system/AnimatedComponents';
import BlurAmountView from 'design-system/BlurAmountView';
import { incomeToPeriod, MAX_BUDGET_AMOUNT, toFrequencyText, toPeriodCadenceText, } from 'features/budgeting/utils';
import Alert from 'utils/packages/Alert';
import { selectIsScrambled, useTextInput } from 'hooks';
import Amplitude from 'utils/amplitude';
import { formatAmount, formatPlural } from 'utils/formatting';
import Links from 'utils/links';
import createStyleSheets from 'utils/createStyleSheets';
import { MoneyTextInput } from 'utils/packages/moneyInput';
import { useMoneyInputText } from 'hooks/useMoneyInputText';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import { useBudgetRollover } from 'features/budgeting/hooks/useBudgetRollover';
import useAppRating from 'hooks/useAppRating';
import { useAppDispatch, useAppSelector, useAppStore } from 'store/hooks';
import { selectIsLoggedIn, selectDefaultCurrency, selectNormalizedCategories, selectHasSubcategories, selectExcludedCategoriesCount, } from 'reducers/selectors';
import HelpButton from 'login/components/HelpButton';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import Amount from 'design-system/Amount';
import { useAutoFocusInput } from 'hooks/useAutoFocusInput';
import useStyles from 'hooks/useStyles';
import { useRequiredBenefitCallbackWithSpaces } from 'features/premium/hooks/useRequiredBenefitCallback';
import { fetchAnalyticsBudgetingData, getIncomes } from 'actions';
import useColors from 'hooks/useColors';
import { KeyboardAvoidingButton } from 'design-system/KeyboardAvoidingButton';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import IconPieChart from 'design-system/icons/IconPieChart';
import useShouldShowUpgrade from 'features/premium/hooks/useShouldShowUpgrade';
import IconCategoryExcluded from 'features/transactions/icons/categories/IconCategoryExcluded';
import { goBackToTopWithStops } from 'utils/navigationv6';
import { updateSetUpEmmaStep } from 'features/setUpEmma/actions';
import { EmmaSetUpStepId } from 'features/feed/components/completeSetup/entities';
import { RollingBudgetsSwitchCard } from '../components/RollingBudgetsSwitchCard';
import { useLoadBudgetsAndIncomes } from '../hooks/useLoadBudgetsAndIncomes';
import { useSaveBudget } from '../hooks/useSaveBudget';
import { selectBudgets, selectIncomes } from '../selectors';
import { isWeb } from '../../../constants';
import { getBudgets } from '../actions';
import { useEditBudgetsCategoryModal } from '../hooks/useEditBudgetsCategoryModal';
import { useBudgetingNavigate } from '../hooks/useBudgetingNavigate';
const strings = {
    ok: 'OK',
    headerTitle: 'Edit budgeting',
    category: 'Category',
    categoryBudgets: 'Category budgets',
    setUpBudget: (cadence) => `How much do you want to spend ${cadence}?`,
    setUpBudgetDescription: 'This should include both your day to day spending and recurring payments',
    categoryBudgetsDescription: 'Category budgets are useful to track your spending in each category. You can set these to control how much you spend on eating out, groceries, and any other category you have set in Emma.',
    choose: 'Choose',
    excludedCategories: 'Excluded categories',
    excludedCategoriesDescription: 'These categories are not included in your Budget or Analytics. You can’t set a budget for them, and their transactions will not count toward your spending totals.',
    budgetingPeriod: 'Period',
    budgetingPeriodDescription: 'The period is the cycle you will use to sync your budgets and analytics. For example, if you get paid on the last working day of each month you can set your period to this so you can track your spending throughout your pay cycle.',
    rollingBudgets: 'Rolling Budgets',
    rollingBudgetsDescription: 'Rolling budgets are a premium feature that makes every budget roll to the next period if they haven’t been filled. This means if you haven’t spent an amount in a cycle, it will be included in the next.',
    totalBudgets: 'Your budgets',
    totalBudgetsDescription: [
        'Your total budget is how much you want to spend in a given period, including both day-to-day spending and recurring payments.',
        'Category budgets let you track and control spending in specific areas, like eating out, groceries, or any other category you’ve set up in Emma.',
        'Your total budget is usually greater than or equal to the sum of all your category budgets.',
    ],
    totalBudgetErrorTitle: 'Total Budget Error!',
    totalBudgetErrorDescription: 'Your total budget can not be null, set it to start saving money. 🤑',
    confirmTitle: 'Confirm your budget',
    confirmDescription: 'You’re ready to start saving with Emma. Review your budget setup below. You can edit it anytime',
};
const EditBudgetScreen = withAnimatedNavigationHeader(({ navigation, route, onScrollY }) => {
    const { flow, editedMonthlyBudget, shouldRollover, budgetState: budgetStateFromRoute, shouldResetRollingAmount, period: periodFromRoute, hasPrefilledBudgets, newPeriod, } = route.params;
    const store = useAppStore();
    const thisMonth = useAppSelector((store) => store.feed.feed.cards.thisMonth);
    const { isUnlockedForSpace, onPressUpgrade } = useShouldShowUpgrade({
        benefitId: 'rollingBudgets',
    });
    const hasSetupBudgets = useMemo(() => thisMonth?.budget != null && !newPeriod, [thisMonth, newPeriod]);
    const styles = useStyles(styleSet);
    const colors = useColors();
    const isLoggedIn = useAppSelector(selectIsLoggedIn);
    useLayoutEffect(() => {
        if (!isLoggedIn) {
            navigation.setOptions({
                headerRight: () => <HelpButton black/>,
            });
        }
    }, [isLoggedIn]);
    const income = useAppSelector(selectIncomes);
    const budgets = useAppSelector(selectBudgets);
    const scrambled = useAppSelector(selectIsScrambled);
    const [period, setPeriod] = useState(periodFromRoute || {});
    const [budgetState, setBudgetState] = useState([]);
    const [IsFocused, setIsFocused] = React.useState(false);
    const handleEvent = useCallback((event) => {
        if (isWeb) {
            setIsFocused(event === 'focus');
        }
    }, []);
    const handleFocus = useCallback(() => {
        handleEvent('focus');
    }, [handleEvent]);
    const handleBlur = useCallback(() => {
        handleEvent('blur');
    }, [handleEvent]);
    useEffect(() => {
        if (periodFromRoute) {
            periodChanged.current = true;
            setPeriod(periodFromRoute);
        }
    }, [periodFromRoute]);
    useEffect(() => {
        // This is when the API returns
        if (budgets && flow !== 'Confirm') {
            setBudgetState(budgets);
        }
    }, [budgets]);
    useEffect(() => {
        if (budgetStateFromRoute !== undefined) {
            setBudgetState(budgetStateFromRoute);
        }
    }, [budgetStateFromRoute]);
    useLoadBudgetsAndIncomes(flow !== 'Confirm');
    useEffect(() => {
        if (flow !== 'Confirm' && !periodFromRoute) {
            setPeriod(incomeToPeriod(income));
        }
    }, [flow, income, periodFromRoute]);
    useEffect(() => {
        if (flow !== 'Confirm') {
            Amplitude.logEvent('Budgets.Start', {
                type: store.getState().user.isLoggedIn ? 'in_app' : 'onboarding',
            });
        }
    }, []);
    const monthly = useMemo(() => budgetState.find((budget) => budget.type === 'overall'), [budgetState]);
    const excludedCategoriesCount = useAppSelector(selectExcludedCategoriesCount);
    const normalizedCategories = useAppSelector(selectNormalizedCategories);
    const hasSubcategories = useAppSelector(selectHasSubcategories);
    const categoryBudgets = useMemo(() => budgetState.filter((budget) => budget.type === 'category' &&
        (budget.baseLimit != null ||
            (budget.totalLimit &&
                budget.totalLimit > 0 &&
                flow === 'Confirm')) &&
        !normalizedCategories[budget.categoryId || '']?.parentCategoryId &&
        (!budget.categoryId ||
            !normalizedCategories[budget.categoryId] ||
            normalizedCategories[budget.categoryId]?.canSetBudget)), [budgetState, flow, normalizedCategories]);
    let initialBudgetValue;
    if (flow === 'Set' && !hasSetupBudgets) {
        initialBudgetValue = '';
    }
    else {
        initialBudgetValue =
            editedMonthlyBudget
                ?.toString()
                .replace(/(\.\d*[1-9])0+$|\.0+$/, '$1') ??
                monthly?.totalLimit
                    ?.toString()
                    .replace(/(\.\d*[1-9])0+$|\.0+$/, '$1') ??
                '';
    }
    const [monthlyBudget, setMonthlyBudget, , monthlyBudgetRef] = useTextInput(Number(initialBudgetValue) <= 0
        ? null
        : Math.min(Number(initialBudgetValue), MAX_BUDGET_AMOUNT));
    const rawMonthlyBudget = useMemo(() => monthlyBudget ?? 0, [monthlyBudget]);
    useEffect(() => {
        if (editedMonthlyBudget !== undefined) {
            setMonthlyBudget(Math.min(editedMonthlyBudget, MAX_BUDGET_AMOUNT));
        }
    }, [editedMonthlyBudget, setMonthlyBudget]);
    useAutoFocusInput(flow === 'Set' ? monthlyBudgetRef : null);
    const toggleSwitchState = useCallback(() => {
        setButtonState((prev) => !prev);
    }, []);
    const { isRollover, setRollover, setRolloverLocal, setSaveHandler } = useBudgetRollover(undefined, toggleSwitchState, flow);
    const [buttonState, setButtonState] = useState(shouldRollover !== undefined ? shouldRollover : isRollover);
    useEffect(() => {
        setButtonState(isRollover);
    }, [isRollover]);
    useEffect(() => {
        if (shouldRollover !== undefined) {
            setRolloverLocal(shouldRollover);
        }
    }, [setRolloverLocal, shouldRollover]);
    const hasEmmaSetUpBudgetIntroRoute = useMemo(() => !!navigation
        .getState()
        .routes.find((route) => route.name === 'EmmaSetUpBudgetIntro'), []);
    const handleToggle = (value) => {
        setButtonState(!buttonState);
        setRollover(value);
    };
    const onChangeValue = useCallback((newValue) => {
        setMonthlyBudget(newValue);
    }, [setMonthlyBudget]);
    const [monthlyBudgetText, onChangeMonthlyBudgetText] = useMoneyInputText(monthlyBudget, onChangeValue);
    const onScroll = useCallback(({ nativeEvent: { contentOffset }, }) => {
        onScrollY(contentOffset.y);
    }, [onScrollY]);
    const dispatch = useAppDispatch();
    const [saving, save] = useSaveBudget();
    const periodChanged = useRef(false);
    const wasKeyboardVisible = useRef(false);
    const { isUnlockedForSpace: isBudgetingUnlocked } = useShouldShowUpgrade({
        benefitId: 'budgeting',
    });
    const onTooltipModalShown = useCallback(() => {
        if (monthlyBudgetRef.current?.isFocused()) {
            wasKeyboardVisible.current = true;
            monthlyBudgetRef.current?.blur();
        }
    }, [monthlyBudgetRef]);
    const onTooltipModalHidden = useCallback(() => {
        if (wasKeyboardVisible.current) {
            wasKeyboardVisible.current = false;
            monthlyBudgetRef.current?.focus();
        }
    }, [monthlyBudgetRef]);
    const onHelpPress = useCallback(() => {
        Links.openKnownLink('budgetingHelp');
    }, []);
    const onSaveBudgetInEmmaSetUpFlow = useCallback(() => {
        navigation.navigate('SetUpEmmaBudgetPlanNoted');
    }, []);
    const validateTotalBudget = useCallback(() => {
        if (!Number(rawMonthlyBudget)) {
            Alert.alert(strings.totalBudgetErrorTitle, strings.totalBudgetErrorDescription, [
                {
                    onPress: () => {
                        monthlyBudgetRef.current?.focus();
                    },
                    text: strings.ok,
                },
            ]);
            return false;
        }
        return true;
    }, [monthlyBudgetRef, rawMonthlyBudget]);
    const { onSuccess } = useBudgetingNavigate();
    const onPressSave = useRequiredBenefitCallbackWithSpaces(async () => {
        if (!Number(rawMonthlyBudget)) {
            Alert.alert('Hold on 💫', 'You need to set a valid total budget.', [
                {
                    onPress: () => monthlyBudgetRef.current?.focus(),
                    text: 'OK',
                },
            ]);
            return;
        }
        const result = await saveBudget({
            monthlyBudget: Number(rawMonthlyBudget),
            budgetState,
            shouldRollover: isRollover,
            resetRollingAccumulated: shouldResetRollingAmount || false,
        });
        if (result) {
            dispatch(getBudgets());
            dispatch(getIncomes());
            dispatch(fetchAnalyticsBudgetingData());
            if (flow === 'Confirm') {
                Amplitude.logEvent('BudgetsSetup.Finish');
                if (store.getState().user.isLoggedIn || isWeb) {
                    navigation.navigateDeprecated('Success', {
                        title: 'Success',
                        subTitle: 'Your budget has been set!',
                        onContinue: () => {
                            if (hasEmmaSetUpBudgetIntroRoute) {
                                dispatch(updateSetUpEmmaStep(EmmaSetUpStepId.SETUP_BUDGET, true));
                            }
                            goBackToTopWithStops(navigation, [
                                'Report',
                                'ContinueEmmaSetUp',
                                'SetUpEmmaSteps',
                            ]);
                            if (!hasEmmaSetUpBudgetIntroRoute) {
                                onSuccess();
                            }
                            return true;
                        },
                    });
                }
                else {
                    const route = 'SetBudgetsSuccess';
                    navigation.navigateDeprecated(route);
                }
            }
            else {
                navigation.goBack();
            }
        }
    }, 'budgeting');
    const rateApp = useAppRating();
    const saveBudget = useCallback(async ({ monthlyBudget, budgetState, shouldRollover, resetRollingAccumulated, }) => {
        if (!validateTotalBudget()) {
            return false;
        }
        const res = await save(monthlyBudget, shouldRollover, budgetState, period && periodChanged.current ? period : undefined, resetRollingAccumulated);
        if (res === null)
            return false;
        const [budgetResult, incomeResult] = res;
        if (budgetResult !== null &&
            (period === undefined || incomeResult !== null)) {
            rateApp();
            dispatch(getBudgets());
        }
        return true;
    }, [period, rateApp, save, validateTotalBudget]);
    useEffect(() => setSaveHandler((isRollover) => saveBudget({
        monthlyBudget: Number(rawMonthlyBudget) || undefined,
        budgetState,
        shouldRollover: isRollover,
        resetRollingAccumulated: shouldResetRollingAmount || false,
    })), [
        budgetState,
        rawMonthlyBudget,
        saveBudget,
        setSaveHandler,
        shouldResetRollingAmount,
    ]);
    const onPressExcluded = useCallback(() => {
        navigation.navigateDeprecated('ExcludeCategories', {
            onSaveLocal: setBudgetState,
            skipSaveBudget: flow === 'Confirm',
            budgets: budgetState,
            ...(flow === 'Confirm' && {
                title: 'Want to exclude categories from your budget?',
                subtitle: 'Choose categories to exclude from Budgeting and Analytics. Transactions in these categories won’t be included in your spending totals.',
            }),
        });
    }, [budgetState, flow]);
    const onPressPeriod = () => {
        Keyboard.dismiss();
        if (!validateTotalBudget()) {
            return;
        }
        const state = store.getState();
        if ((state.deviceSettings.didSetPeriod || periodFromRoute) &&
            flow === 'Set') {
            navigation.navigateDeprecated('Budgets', {
                period,
                monthlyBudget: Number(rawMonthlyBudget),
                budgetState,
                flow,
            });
        }
        else {
            navigation.navigateDeprecated('SelectPeriod', {
                period,
                monthlyBudget: Number(rawMonthlyBudget),
                budgetState,
                flow,
                hasPrefilledBudgets,
            });
        }
    };
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const defaultCurrency = useAppSelector(selectDefaultCurrency);
    const showEditModal = useEditBudgetsCategoryModal({
        onEdit: () => {
            navigation.navigateDeprecated('Budgets', {
                monthlyBudget: Number(rawMonthlyBudget),
                budgetState,
                shouldRollover: isRollover,
                period,
                flow,
            });
        },
        category: {
            shouldRollover: true,
            totalLimit: monthly?.totalLimit,
            baseLimit: monthly?.baseLimit,
            rollingAccumulatedLimit: monthly?.rollingAccumulatedLimit,
            currency: monthly?.currency || defaultCurrency,
            displayName: 'total',
        },
        isExplainZeroBudget: true,
        title: 'Why total budget is 0?',
        customIcon: <IconCoins alt gradientKey="plus"/>,
    });
    const onPressEditTotalBudget = () => {
        if (flow !== 'Confirm' &&
            monthly?.totalLimit != null &&
            monthly.totalLimit <= 0) {
            showEditModal();
        }
        else {
            navigation.navigateDeprecated('Budgets', {
                monthlyBudget: Number(rawMonthlyBudget),
                budgetState,
                shouldRollover: isRollover,
                period,
                flow,
            });
        }
    };
    return (<AppView withFrame={false}>
        <AnimatedKeyboardAwareScrollView scrollEventThrottle={8} onScroll={onScroll} enableAutomaticScroll={flow !== 'Set'} keyboardShouldPersistTaps="always" contentContainerStyle={paddingHorizontalStyle} showsVerticalScrollIndicator={false}>
          <StatusBar barStyle="dark-content"/>
          {flow === 'Confirm' && (<ScreenInfoHeader title={strings.confirmTitle} subTitle={strings.confirmDescription}/>)}

          {flow === 'Set' && (<View style={styles.contentContainer}>
              <ScreenInfoHeader title={strings.setUpBudget(toPeriodCadenceText(period))} subTitle={strings.setUpBudgetDescription}/>

              <View style={styles.inputWrapper}>
                <View style={[styles.inputContainer, IsFocused && styles.focused]}>
                  <View style={styles.inputLeftContainer}>
                    <Text Text-24 style={styles.inputTextLeft}>
                      {getSymbolFromCurrency(monthly?.currency || defaultCurrency)}
                    </Text>
                    <View style={styles.verticalSeparator}/>
                  </View>
                  <View style={styles.flex}>
                    <MoneyTextInput placeholder={'0'} onBlur={handleBlur} value={monthlyBudgetText} onFocus={handleFocus} onChangeText={onChangeMonthlyBudgetText} ref={monthlyBudgetRef} maxValue={MAX_BUDGET_AMOUNT} style={styles.textInput} underlineColorAndroid="transparent"/>
                  </View>
                </View>

                {monthly?.rollingAccumulatedLimit ? (<View>
                    <BlurAmountView enabled={scrambled}>
                      <Text TextThin-14 Secondary centered>
                        Default{' '}
                        <Text NumbersThin-14 Secondary centered>
                          {formatAmount(monthly?.baseLimit, monthly?.currency, 2)}
                        </Text>
                      </Text>
                    </BlurAmountView>
                    <BlurAmountView enabled={scrambled}>
                      {!isUnlockedForSpace ? <Spacer h={4}/> : null}
                      <Text TextThin-14 centered color={monthly?.rollingAccumulatedLimit >= 0
                    ? colors.status.positive
                    : colors.status.negative} {...(!isUnlockedForSpace ? { Gray: true } : {})}>
                        Rolled{' '}
                        {!isUnlockedForSpace ? (<Button title="Upgrade" brandReversed square cards extra small hug onPress={onPressUpgrade}/>) : (<Amount Numbers-14 showSign color={monthly?.rollingAccumulatedLimit >= 0
                        ? colors.status.positive
                        : colors.status.negative} value={{
                        value: monthly.rollingAccumulatedLimit,
                        currency: monthly?.currency,
                    }}/>)}
                      </Text>
                    </BlurAmountView>
                  </View>) : (<BlurAmountView enabled={scrambled}>
                    <Text TextThin-14 Secondary centered>
                      3-months avg{' '}
                      <Text NumbersThin-14 Secondary centered>
                        {formatAmount(monthly?.previousPeriodAverage || 0, monthly?.currency, 2)}
                      </Text>
                    </Text>
                  </BlurAmountView>)}
              </View>
            </View>)}
          {flow !== 'Set' && (<>
              <SettingsSection webPadding {...(flow === 'Confirm' && { noMarginTop: true })}>
                <SettingsItemNavigate icon={<IconPieChart gradientKey="deepPink"/>} onPress={onPressEditTotalBudget}>
                  <View style={styles.textWithIconContainer}>
                    <SettingsItemDescription>
                      {strings.totalBudgets}
                    </SettingsItemDescription>
                    <View style={styles.infoIconContainer}>
                      <ToolTip small offset={1} title={strings.totalBudgets} subtitle={strings.totalBudgetsDescription} onModalShown={onTooltipModalShown} onModalHidden={onTooltipModalHidden}/>
                    </View>
                  </View>

                  <Spacer h={4}/>

                  <BlurAmountView enabled={scrambled}>
                    <Text Text-16>
                      <Text Numbers-16>
                        {formatAmount(monthly?.totalLimit && monthly.totalLimit > 0
                ? monthly.totalLimit
                : 0, monthly?.currency, 2)}
                      </Text>
                      {categoryBudgets.length
                ? ` allocated to ${categoryBudgets.length} ${hasSubcategories ? 'main ' : ''}${categoryBudgets.length === 1
                    ? 'category'
                    : 'categories'}`
                : ''}
                    </Text>
                  </BlurAmountView>

                  {monthly ? (<View style={styles.row}>
                      <SettingsItemSubDescription>
                        {monthly?.rollingAccumulatedLimit ? (<Text TextThin-14 color={monthly?.rollingAccumulatedLimit >= 0
                        ? colors.status.positive
                        : colors.status.negative} {...(!isUnlockedForSpace ? { Gray: true } : {})}>
                            {'Rolled '}
                          </Text>) : ('Avg ')}
                      </SettingsItemSubDescription>

                      {!isUnlockedForSpace &&
                    monthly.rollingAccumulatedLimit ? (<>
                          <Spacer w={2}/>
                          <Button title="Upgrade" brandReversed square cards extra small hug onPress={onPressUpgrade}/>
                        </>) : (<Amount {...(!monthly?.rollingAccumulatedLimit
                    ? { Gray: true, 'NumbersThin-14': true }
                    : { 'Numbers-14': true })} showSign={!!monthly?.rollingAccumulatedLimit} color={(monthly?.rollingAccumulatedLimit || 0) >= 0
                        ? colors.status.positive
                        : colors.status.negative} value={{
                        value: monthly.rollingAccumulatedLimit
                            ? monthly.rollingAccumulatedLimit
                            : monthly.previousPeriodAverage || 0,
                        currency: monthly?.currency,
                    }}/>)}
                    </View>) : null}
                </SettingsItemNavigate>

                <SettingsItemNavigate icon={<IconCategoryExcluded width={rem(24)} height={rem(24)} fill={colors.elements.secondary} viewBox="0 0 24 24"/>} onPress={onPressExcluded}>
                  <View style={styles.textWithIconContainer}>
                    <SettingsItemDescription>
                      {strings.excludedCategories}
                    </SettingsItemDescription>
                    <View style={styles.infoIconContainer}>
                      <ToolTip small offset={1} title={strings.excludedCategories} subtitle={strings.excludedCategoriesDescription} onModalShown={onTooltipModalShown} onModalHidden={onTooltipModalHidden}/>
                    </View>
                  </View>
                  <SettingsItemTitle>
                    {excludedCategoriesCount} {hasSubcategories ? 'main ' : ''}
                    {formatPlural('category', 'categories', excludedCategoriesCount)}
                  </SettingsItemTitle>
                </SettingsItemNavigate>

                <SettingsItemNavigate icon={<IconBoldCalendar gradientKey="skyBlue"/>} onPress={onPressPeriod}>
                  <View style={styles.textWithIconContainer}>
                    <SettingsItemDescription>
                      {strings.budgetingPeriod}
                    </SettingsItemDescription>
                    <View style={styles.infoIconContainer}>
                      <ToolTip small offset={1} title={strings.budgetingPeriod} subtitle={strings.budgetingPeriodDescription} onModalShown={onTooltipModalShown} onModalHidden={onTooltipModalHidden}/>
                    </View>
                  </View>
                  <SettingsItemTitle>
                    {toFrequencyText(period, strings.choose)}
                  </SettingsItemTitle>
                </SettingsItemNavigate>
                <RollingBudgetsSwitchCard value={buttonState} isFetching={saving} onValueChange={handleToggle} onModalShown={onTooltipModalShown} onModalHidden={onTooltipModalHidden}/>
                {monthly?.rollingAccumulatedLimit && !isUnlockedForSpace ? (<View style={styles.rollingUpgradeSection}>
                    <View>
                      <Text Text-14 color={colors.status.offline}>
                        Rolling was on
                      </Text>
                      <Text TextThin-14 Secondary>
                        Want to see rolled amount?
                      </Text>
                    </View>

                    <View style={styles.rollingUpgradeSectionButtonContainer}>
                      <Button title="Upgrade" brandReversed square hug {...(isWeb ? { cards: true } : {})} onPress={onPressUpgrade}/>
                    </View>
                  </View>) : null}
              </SettingsSection>

              {isLoggedIn ? (<>
                  <Spacer.H16 />
                  <HelpCallToAction onDark onPress={onHelpPress} subTitle="Learn how to do it" title="Need to amend your budgets?"/>
                </>) : null}
            </>)}
        </AnimatedKeyboardAwareScrollView>
        {flow === 'Set' &&
            (hasSetupBudgets ? (<KeyboardAvoidingButton brand title="Save" isFetching={saving} onPress={onPressSave}/>) : (<KeyboardAvoidingButton brand style={styles.button} preventDoubleTap title="Continue" disabled={monthlyBudget == null || !rawMonthlyBudget} onPress={onPressPeriod}/>))}

        {flow === 'Confirm' && (<Button brand floating title="Continue" isFetching={saving} onPress={hasEmmaSetUpBudgetIntroRoute && !isBudgetingUnlocked
                ? onSaveBudgetInEmmaSetUpFlow
                : onPressSave}/>)}
      </AppView>);
}, ({ route }) => ({
    pageTitle: getPageTitle(route.params.flow),
    title: (() => {
        if (route.params.flow === 'Edit') {
            return strings.headerTitle;
        }
        return '';
    })(),
}));
const getPageTitle = (flow) => {
    if (flow === 'Set') {
        return 'Set Budget';
    }
    if (flow === 'Confirm') {
        return 'Confirm Budget';
    }
    return 'Edit Budget';
};
const styleSet = createStyleSheets((colors) => ({
    setUpBudgetContainer: {
        paddingHorizontal: rem(16),
        paddingTop: rem(10),
        marginBottom: rem(16),
    },
    contentContainer: {
        flex: 1,
        justifyContent: 'space-between',
        paddingBottom: rem(24),
    },
    button: {
        bottom: 0,
    },
    setUpBudgetText: { marginBottom: rem(9) },
    textWithIconContainer: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    infoIconContainer: {
        marginLeft: rem(4),
    },
    inputWrapper: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: rem(169),
        marginHorizontal: rem(16),
        marginTop: rem(16),
    },
    inputContainer: {
        backgroundColor: colors.cards.onDark,
        width: rem(270),
        borderRadius: rem(12),
        marginBottom: rem(13),
        flexDirection: 'row',
        alignItems: 'center',
        ...(isWeb && {
            borderWidth: 1,
            borderColor: colors.cards.onDark,
            caretColor: colors.text.selection,
        }),
    },
    focused: {
        ...(isWeb && {
            borderWidth: 1,
            borderColor: colors.elements.brand,
        }),
    },
    inputLeftContainer: {
        flexDirection: 'row',
    },
    inputTextLeft: {
        paddingVertical: rem(11.5),
        paddingHorizontal: rem(20),
    },
    verticalSeparator: {
        backgroundColor: colors.background.dark,
        width: 1,
    },
    pinPad: {
        marginBottom: rem(16),
    },
    textInput: {
        ...styles.Numbers_24,
        flex: 1,
        textAlign: 'right',
        color: colors.text.primary,
        paddingHorizontal: rem(16),
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    learnMore: {
        alignSelf: 'flex-start',
    },
    infoCard: {
        marginLeft: rem(8),
    },
    headerPadding: {
        paddingHorizontal: rem(16),
        paddingBottom: rem(24),
    },
    rollingUpgradeSection: {
        marginHorizontal: rem(16),
        marginBottom: rem(16),
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    rollingUpgradeSectionButtonContainer: {
        justifyContent: 'flex-end',
    },
    flex: {
        flex: 1,
    },
}));
export default EditBudgetScreen;
